import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Lightweight signature pad — pure React + Canvas, no external deps.
 *
 * Captures a hand-drawn signature using mouse OR touch (works on
 * iPad/iPhone/Android tablets). Fills the canvas area with a clean white
 * background and dark navy strokes, then exposes a `getDataURL()`-style
 * callback that returns a base64 PNG suitable for the backend's
 * `POST /api/upload/signature/{member_id}/digital` endpoint.
 *
 * Why no library? Avoids adding 30 KB just to draw lines. The
 * implementation here is < 100 lines and supports pressure-insensitive
 * pointer/touch input which is the de-facto standard for signature pads.
 */
export default function SignaturePad({ width = 480, height = 180, onSave, onCancel, lang = "ro" }) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);

  // Initialise the canvas with a white background + dark gold border.
  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    cvs.width = width;
    cvs.height = height;
    const ctx = cvs.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = "#0d1b2a";
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, [width, height]);

  const eventToXY = (e) => {
    const cvs = canvasRef.current;
    const r = cvs.getBoundingClientRect();
    const t = e.touches?.[0];
    const cx = t ? t.clientX : e.clientX;
    const cy = t ? t.clientY : e.clientY;
    return { x: ((cx - r.left) / r.width) * cvs.width, y: ((cy - r.top) / r.height) * cvs.height };
  };

  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    const { x, y } = eventToXY(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = eventToXY(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasInk) setHasInk(true);
  };
  const end = () => setDrawing(false);

  const clear = () => {
    const cvs = canvasRef.current;
    const ctx = cvs.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, cvs.width, cvs.height);
    setHasInk(false);
  };

  const save = () => {
    if (!hasInk) return;
    const dataURL = canvasRef.current.toDataURL("image/png");
    onSave?.(dataURL);
  };

  const en = lang === "en";

  return (
    <div data-testid="signature-pad-root">
      <div style={{
        background: "#fff",
        border: "2px solid #c9a84c",
        borderRadius: 6,
        padding: 4,
        display: "inline-block",
      }}>
        <canvas
          ref={canvasRef}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          style={{ touchAction: "none", display: "block", cursor: "crosshair" }}
          data-testid="signature-pad-canvas"
        />
      </div>
      <p className="kv-label" style={{ fontSize: "0.66rem", marginTop: 6, color: "#9a7530" }}>
        {t("signaturePad.sign_with_your_finger_touch")}
      </p>
      <div className="flex gap-2 mt-2 justify-end">
        <button type="button" className="btn-outline-gold" onClick={clear} data-testid="signature-pad-clear">
          {t("signaturePad.clear")}
        </button>
        {onCancel ? (
          <button type="button" className="btn-outline-gold" onClick={onCancel} data-testid="signature-pad-cancel">
            {t("signaturePad.cancel")}
          </button>
        ) : null}
        <button type="button" className="btn-primary-gold" onClick={save} disabled={!hasInk} data-testid="signature-pad-save">
          {t("signaturePad.save")}
        </button>
      </div>
    </div>
  );
}
